import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  catalogResponseSchema,
  formResponseSchema,
  userDetailResponseSchema,
  type IssueLineRequest,
} from '@unit/shared';
import { useApi } from '../../lib/useApi.js';
import { apiPost } from '../../lib/api.js';
import { useAction } from '../../lib/useAction.js';
import { ApiError } from '../../components/ApiError.js';
import { ActionError } from '../../components/ActionError.js';
import { MissionSelect } from '../missions/MissionSelect.js';
import { LineEditor, linesComplete, toRequestLines, type Line } from './LineEditor.js';
import { TemplateSelect } from './TemplateSelect.js';
import { personName } from '@unit/shared';

const input =
  'rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-900 focus:border-slate-500 focus:outline-none';
const primary =
  'rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-700 disabled:opacity-40';

/**
 * The issue wizard (REQUIREMENTS §2.5 "Issue", §2.6). One soldier, one or
 * more items, one form. Serialized items get a scan-friendly serial picker;
 * quantity items get a number. The server validates and reserves everything
 * in one transaction; this screen only assembles the request.
 */
export function IssuePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const soldierUid = params.get('soldier') ?? '';

  const soldier = useApi(soldierUid ? `/users/${soldierUid}` : null, userDetailResponseSchema);
  const catalog = useApi('/catalog', catalogResponseSchema);
  const [lines, setLines] = useState<Line[]>([]);
  const [dueDate, setDueDate] = useState('');
  const [missionId, setMissionId] = useState('');
  const submit = useAction((body: { soldierUid: string; lines: IssueLineRequest[]; dueDate: string | null; missionId: string | null }) =>
    apiPost('/issuance', formResponseSchema, body),
  );

  const items = useMemo(
    () => (catalog.kind === 'ready' ? catalog.data.items.filter((i) => i.active) : []),
    [catalog],
  );

  const send = async () => {
    const result = await submit.run({
      soldierUid,
      lines: toRequestLines(lines),
      dueDate: dueDate ? new Date(dueDate).toISOString() : null,
      missionId: missionId || null,
    });
    if (result) navigate(`/forms/${result.form.id}`, { replace: true });
  };

  if (!soldierUid) return <p className="text-slate-500">{t('issue.noSoldier')}</p>;
  if (soldier.kind === 'loading' || catalog.kind === 'loading')
    return <p className="text-slate-500">{t('home.loading')}</p>;
  if (soldier.kind === 'error') return <ApiError code={soldier.code} requestId={soldier.requestId} />;
  if (catalog.kind === 'error') return <ApiError code={catalog.code} requestId={catalog.requestId} />;

  const s = soldier.data.user;
  const name = personName(s);

  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200" data-testid="issue-page">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-900">{t('issue.title', { name })}</h2>
        <Link to={`/org/${s.teamId ?? s.platoonId ?? ''}/soldier/${s.uid}`} className="text-sm text-slate-500 underline-offset-4 hover:underline">
          {t('users.backToRoster')}
        </Link>
      </div>
      <p className="mt-1 text-sm text-slate-500">{t('issue.hint')}</p>

      <div className="mt-3">
        <TemplateSelect items={items} onPick={setLines} />
      </div>
      <LineEditor lines={lines} onChange={setLines} items={items} />

      <div className="mt-4 flex flex-wrap items-end gap-2">
        <MissionSelect
          value={missionId}
          onChange={(id, endAt) => {
            setMissionId(id);
            if (endAt && !dueDate) setDueDate(endAt.slice(0, 10));
          }}
        />
        <label className="text-xs text-slate-600">
          {t('issue.dueDate')}
          <input type="date" className={`${input} latin-inline mt-1 block`} value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="due-date" />
        </label>
      </div>

      <div className="mt-5 flex items-center gap-2">
        <button type="button" onClick={() => void send()} disabled={!linesComplete(lines) || submit.busy} className={primary} data-testid="issue-submit">
          {submit.busy ? t('issue.submitting') : t('issue.submit', { count: lines.length })}
        </button>
      </div>
      <div className="mt-3">
        <ActionError error={submit.error} />
        <IssueErrorDetail error={submit.error} />
      </div>
    </section>
  );
}

/** The one-line explanation under a stock or serial conflict; shared with the form edit. */
export function IssueErrorDetail({ error }: { error: { details: Record<string, unknown> } | null }) {
  const { t } = useTranslation();
  if (!error) return null;
  if (error.details.reason === 'insufficient_stock') {
    return (
      <p className="text-sm text-slate-600">
        {t('issue.insufficient', { available: Number(error.details.available), requested: Number(error.details.requested) })}
      </p>
    );
  }
  if (error.details.reason === 'unit_not_available') {
    return <p className="text-sm text-slate-600">{t('issue.unitTaken', { serial: String(error.details.serialNumber) })}</p>;
  }
  return null;
}
